// components/AnalysisControls.tsx
import React from 'react';
import Select, { MultiValue, ActionMeta } from 'react-select';
import { RadioButton } from 'primereact/radiobutton';
import { BuildingType } from '../../../types/BuildingTypeEnums';
import {
    DateRange,
    TimeGrouping,
    BuildingTypeOption,
    UserGroup
} from '../types/analysis.types';

interface AnalysisControlsProps {
    buildingTypeOptions: BuildingTypeOption[];
    selectedBuildingTypeOptions: BuildingTypeOption[];
    onBuildingTypeChange: (options: BuildingTypeOption[]) => void;
    selectedBuildingTypes: BuildingType[];
    onSelectedBuildingTypesChange: (types: BuildingType[]) => void;
    selectedUserGroup: string | null;
    onUserGroupChange: (groupKey: string | null) => void;
    userGroups: UserGroup[];
    timeGrouping: TimeGrouping;
    onTimeGroupingChange: (grouping: TimeGrouping) => void;
    dateRange: DateRange;
    onDateRangeChange: (range: DateRange) => void;
}

const AnalysisControls: React.FC<AnalysisControlsProps> = ({
                                                               buildingTypeOptions,
                                                               selectedBuildingTypeOptions,
                                                               onBuildingTypeChange,
                                                               selectedBuildingTypes,
                                                               onSelectedBuildingTypesChange,
                                                               selectedUserGroup,
                                                               onUserGroupChange,
                                                               userGroups,
                                                               timeGrouping,
                                                               onTimeGroupingChange,
                                                               dateRange,
                                                               onDateRangeChange
                                                           }) => {
    const handleBuildingTypeChange = (
        newValue: MultiValue<BuildingTypeOption>,
        actionMeta: ActionMeta<BuildingTypeOption>
    ): void => {
        const selectedOptions = Array.from(newValue);
        const selectedTypes = selectedOptions.map(option => option.value);
        onSelectedBuildingTypesChange(selectedTypes);
        onBuildingTypeChange(selectedOptions);
    };

    const handleUserGroupChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        onUserGroupChange(value === "all" ? null : value);
    };

    const handleDateRangeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        onDateRangeChange({
            ...dateRange,
            [name]: value
        });
    };

    return (
        <div className="controls-container">
            <div className="control-group">
                <label className="control-label">Byggnadstyper</label>
                <Select
                    closeMenuOnSelect={false}
                    isMulti
                    options={buildingTypeOptions}
                    value={selectedBuildingTypeOptions}
                    onChange={handleBuildingTypeChange}
                    placeholder="Välj byggnadstyper..."
                    noOptionsMessage={() => "Inga byggnadstyper tillgängliga"}
                    isClearable={false}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            '&:hover': {
                                borderColor: '#999'
                            }
                        }),
                    }}
                />
            </div>

            <div className="control-group">
                <label htmlFor="group-select" className="control-label">Företag</label>
                <select
                    id="group-select"
                    value={selectedUserGroup || "all"}
                    onChange={handleUserGroupChange}
                    className="select-control"
                >
                    <option value="all">Alla företag</option>
                    {userGroups.map(group => (
                        <option key={group.key} value={group.key}>
                            {group.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="radio-group-horizontal">
                <span className="control-label">Tidsperiod</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RadioButton
                            inputId="timeDaily"
                            name="time-grouping"
                            value="daily"
                            onChange={(e) => onTimeGroupingChange(e.value as TimeGrouping)}
                            checked={timeGrouping === 'daily'}
                        />
                        <label htmlFor="timeDaily" style={{ marginLeft: '8px' }}>Dag</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RadioButton
                            inputId="timeWeekly"
                            name="time-grouping"
                            value="weekly"
                            onChange={(e) => onTimeGroupingChange(e.value as TimeGrouping)}
                            checked={timeGrouping === 'weekly'}
                        />
                        <label htmlFor="timeWeekly" style={{ marginLeft: '8px' }}>Vecka</label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RadioButton
                            inputId="timeMonthly"
                            name="time-grouping"
                            value="monthly"
                            onChange={(e) => onTimeGroupingChange(e.value as TimeGrouping)}
                            checked={timeGrouping === 'monthly'}
                        />
                        <label htmlFor="timeMonthly" style={{ marginLeft: '8px' }}>Månad</label>
                    </div>
                </div>
            </div>

            <div className="control-group">
                <label htmlFor="date-start" className="control-label">Från datum</label>
                <input
                    id="date-start"
                    type="date"
                    name="start"
                    value={dateRange.start}
                    onChange={handleDateRangeChange}
                    className="date-control"
                />
            </div>

            <div className="control-group">
                <label htmlFor="date-end" className="control-label">Till datum</label>
                <input
                    id="date-end"
                    type="date"
                    name="end"
                    value={dateRange.end}
                    onChange={handleDateRangeChange}
                    className="date-control"
                />
            </div>
        </div>
    );
};

export default AnalysisControls;